const DEFAULT_MAX_BUFFER_BYTES = 1048576; // 1 MiB
/**
 * Parse one SSE frame (lines joined, terminated by a blank line) into event name + JSON data.
 * Comment lines (`:…`) are ignored.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events#event_stream_format
 */ function parseSseFrame(part) {
    let eventName = "message";
    const dataLines = [];
    for (const line of part.split("\n")){
        if (line.startsWith(":")) {
            continue;
        }
        if (line.startsWith("event:")) {
            eventName = line.slice(6).replace(/^\s/, "");
            continue;
        }
        if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).replace(/^\s/, ""));
        }
    }
    if (dataLines.length === 0) {
        return null;
    }
    const rawData = dataLines.join("\n");
    if (!rawData || rawData === "[DONE]") {
        return null;
    }
    return {
        event: eventName,
        data: JSON.parse(rawData)
    };
}
/**
 * Parse an SSE byte stream into `{ event, data }` items (`event:` + JSON `data:`).
 * Cancels the underlying reader on abort, early exit, or buffer overflow.
 */ async function* parseSseJsonStream(body, signal, options) {
    const maxBufferBytes = options?.maxBufferBytes ?? DEFAULT_MAX_BUFFER_BYTES;
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const onAbort = ()=>{
        void reader.cancel().catch(()=>{
        // Already closed or cancelled.
        });
    };
    signal?.addEventListener("abort", onAbort, {
        once: true
    });
    try {
        while(!signal?.aborted){
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, {
                stream: true
            });
            if (buffer.length > maxBufferBytes) {
                throw new Error(`SSE reassembly buffer exceeded ${maxBufferBytes} bytes`);
            }
            const parts = buffer.split("\n\n");
            buffer = parts.pop() ?? "";
            for (const part of parts){
                if (signal?.aborted) {
                    return;
                }
                const parsed = parseSseFrame(part);
                if (!parsed) {
                    continue;
                }
                yield {
                    event: parsed.event,
                    data: parsed.data
                };
            }
        }
    } finally{
        signal?.removeEventListener("abort", onAbort);
        try {
            await reader.cancel();
        } catch  {
        // Already closed or cancelled.
        }
    }
}
/**
 * Wrap an SSE body as an async iterable plus an idempotent `close()` that cancels the stream
 * (and honors an optional external {@link AbortSignal}).
 */ function createEventSourceEvents(body, signal, options) {
    const streamAbort = new AbortController();
    const onExternalAbort = ()=>streamAbort.abort();
    signal?.addEventListener("abort", onExternalAbort, {
        once: true
    });
    const close = ()=>{
        streamAbort.abort();
        signal?.removeEventListener("abort", onExternalAbort);
    };
    const generator = parseSseJsonStream(body, streamAbort.signal, options);
    return {
        [Symbol.asyncIterator] () {
            return generator;
        },
        close
    };
}
export { parseSseJsonStream, parseSseFrame, createEventSourceEvents, DEFAULT_MAX_BUFFER_BYTES };
